import dayjs from 'dayjs';
import colors from './colors';
import eventTypes from './eventTypes';
import currency from 'helpers/currency';

/**
 * Tons e textos do redesign do app (theme/tokens e helpers/cardData do
 * ENG_APP_Platform), trazidos para a home e o detalhe do site. O roxo é o do
 * site, não o do app, para não destoar do resto das páginas.
 */
export const ui = {
    ink: '#15132A',
    inkSoft: '#6E6C82',
    inkFaint: '#9A98AC',
    surfaceMuted: '#F7F6FB',
    surfaceSunken: '#EEECF4',
    border: '#ECEAF3',
    tagBg: '#F3EAFF',
    link: colors.occasionalPurple,
    overlayButton: 'rgba(255, 255, 255, 0.92)',
    cta: `linear-gradient(135deg, #8B3CF7 0%, ${colors.occasionalPurple} 50%, #5207C2 100%)`,
    cardShadow: '0 2px 12px rgba(21, 19, 42, 0.06), 0 1px 2px rgba(21, 19, 42, 0.04)',
    cardShadowHover: '0 10px 28px rgba(21, 19, 42, 0.12), 0 2px 6px rgba(21, 19, 42, 0.06)',
    // Scrim dos cartões com texto sobre a foto: escurece cedo porque os
    // cartazes reais são claros e cheios de texto.
    scrimCard: 'linear-gradient(to bottom, rgba(8, 4, 24, 0) 0%, rgba(8, 4, 24, 0.1) 25%, rgba(8, 4, 24, 0.6) 50%, rgba(8, 4, 24, 0.92) 100%)',
};

export const PLACEHOLDER_EVENT = '/assets/images/placeholder-events.png';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "Sáb, 25 out". Sem o ano: quem abre a home procura o que fazer nos próximos dias. */
export function shortDate(value) {
    const d = dayjs(value);
    if (!d.isValid()) return '';

    return `${WEEKDAYS[d.day()]}, ${d.date()} ${MONTHS[d.month()]}`;
}

/**
 * "20:00:00" → "20h"; "20:30:00" → "20h30". A API às vezes manda data e hora
 * juntas, por isso procura o HH:MM em vez de partir a string.
 */
export function shortTime(value) {
    if (!value) return '';

    const match = String(value).match(/(?:^|[T\s])(\d{1,2}):(\d{2})/);
    if (!match) return '';

    const [, hour, minute] = match;
    return minute !== '00' ? `${Number(hour)}h${minute}` : `${Number(hour)}h`;
}

/** "Sáb, 25 out • 20h", ou só a data quando não há horário. */
export function eventWhen(event) {
    const first = event?.dates?.[0];
    if (!first?.date) return 'Data a definir';

    const time = shortTime(first.start);
    return time ? `${shortDate(first.date)} • ${time}` : shortDate(first.date);
}

/**
 * "Gratuito" ou "A partir de R$ 25,00". `starting_price` é o menor ingresso à
 * venda agora; os destaques mandam `min_price`/`is_free`. Sem preço conhecido
 * (importado, esgotado, só produtos), nada — "Pago" sozinho não ajuda a decidir.
 */
export function eventPrice(event) {
    const price = event?.starting_price ?? event?.min_price;
    const isFree = event?.is_free ?? (event?.classification?.name === eventTypes.free.slug);

    if (isFree || price === 0) return eventTypes.free.label;
    if (Number(price) > 0) return `A partir de ${currency.format(Number(price))}`;

    return null;
}

/** Aberto agora: o detalhe manda `dates.open`, a listagem manda `open`. */
export function placeIsOpen(place) {
    return !!(place?.open ?? place?.dates?.open);
}

/** "4,6" quando o lugar tem avaliações; nada quando ainda não tem. */
export function placeRating(place) {
    const average = Number(place?.rating?.average);
    if (!place?.rating?.count || !Number.isFinite(average) || average <= 0) return null;

    return average.toFixed(1).replace('.', ',');
}

/** "Bar • Centro": a primeira categoria e o bairro (ou a rua, ou a cidade). */
export function placeSummary(place) {
    const address = place?.address;
    const area = address?.district || address?.name || address?.street || address?.city;

    return [place?.tagsEstablishments?.[0]?.name, area].filter(Boolean).join(' • ');
}

/** O lugar numa linha curta: nome do local, bairro ou rua, cidade. */
export function eventPlace(event) {
    const address = event?.address;

    return address?.name
        || address?.street?.district
        || address?.street?.name
        || address?.raw_address
        || address?.city_name
        || address?.street?.city?.name
        || 'Local a definir';
}
